/**
 * StripeOnboarding Component
 * Permette agli utenti UISP (regionali/territoriali/nazionali) di configurare
 * il proprio account Stripe Connect per ricevere pagamenti
 */

import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const StripeOnboarding = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [error, setError] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);

  useEffect(() => {
    // Verifica che l'utente sia autorizzato
    if (!user || !['admin', 'uisp_national', 'uisp_regional', 'uisp_territorial'].includes(user.role)) {
      setError('Non sei autorizzato ad accedere a questa sezione');
      setLoading(false);
      return;
    }

    checkOnboardingStatus();
  }, [user]);

  const checkOnboardingStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/payments/connect/onboarding-status');
      if (response.data.success) {
        setOnboardingStatus(response.data.data);
      }
    } catch (err) {
      console.error('Error checking onboarding status:', err);
      // Se 503 o messaggio specifico: Stripe non configurato sul server
      const msg = err.response?.data?.message || '';
      if (err.response?.status === 503 || msg.toLowerCase().includes('stripe') && msg.toLowerCase().includes('config')) {
        setError('stripe_not_configured');
      } else {
        setError(err.response?.data?.message || 'Errore nel recupero dello stato di configurazione');
      }
    } finally {
      setLoading(false);
    }
  };

  const startOnboarding = async () => {
    try {
      setGeneratingLink(true);
      setError(null);

      const response = await api.get('/payments/connect/onboarding-link');
      
      if (response.data.success && response.data.data.url) {
        // Reindirizza l'utente a Stripe per completare l'onboarding
        window.location.href = response.data.data.url;
      } else {
        throw new Error('Impossibile generare il link di onboarding');
      }
    } catch (err) {
      console.error('Error starting onboarding:', err);
      setError(err.response?.data?.message || 'Errore nell\'avvio della configurazione');
      setGeneratingLink(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      uisp_national: 'UISP Nazionale',
      uisp_regional: 'UISP Regionale',
      uisp_territorial: 'UISP Territoriale',
      admin: 'Amministratore'
    };
    return labels[role] || role;
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" style={{ color: '#006e39' }} />
        <p className="mt-3">Caricamento...</p>
      </Container>
    );
  }

  if (error === 'stripe_not_configured') {
    return (
      <Container className="mt-4">
        <h2 style={{ color: '#006e39' }}>
          <i className="bi bi-lightning-charge me-2"></i>
          Configurazione pagamenti Stripe
        </h2>
        <Alert variant="warning" className="mt-3">
          <Alert.Heading>
            <i className="bi bi-exclamation-triangle me-2"></i>
            Stripe non configurato sul server
          </Alert.Heading>
          <p>
            Per abilitare i pagamenti online, aggiungi le seguenti variabili al file <code>.env</code> sul server:
          </p>
          <pre style={{ background: '#fff3cd', padding: '12px', borderRadius: '4px', fontSize: '0.85em' }}>
{`STRIPE_SECRET_KEY=sk_live_...\nSTRIPE_PUBLISHABLE_KEY=pk_live_...\nSTRIPE_WEBHOOK_SECRET=whsec_...`}
          </pre>
          <p className="mb-0">
            Poi riavvia il backend con <code>pm2 restart uisp-backend --update-env</code>.
          </p>
        </Alert>
        <Alert variant="info">
          <strong>In alternativa</strong>, puoi configurare i <strong>Conti Bancari</strong> per ricevere pagamenti via bonifico,
          che non richiedono Stripe.
        </Alert>
      </Container>
    );
  }

  if (error && !onboardingStatus) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Errore</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" size="sm" onClick={checkOnboardingStatus}>
            <i className="bi bi-arrow-clockwise me-2"></i>Riprova
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <h2 style={{ color: '#006e39' }}>
        <i className="bi bi-stripe me-2"></i>
        Configurazione pagamenti Stripe
      </h2>
      <p className="text-muted">
        Configura il tuo account Stripe Connect per ricevere pagamenti dagli eventi
      </p>

      <Card className="shadow-sm mb-4">
        <Card.Header style={{ backgroundColor: '#006e39', color: 'white' }}>
          <h5 className="mb-0">Stato configurazione</h5>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <div className="mb-3">
            <strong>Ruolo:</strong>{' '}
            <Badge bg="primary" className="ms-2">
              {getRoleLabel(user.role)}
            </Badge>
          </div>

          {onboardingStatus && (
            <>
              <div className="mb-3">
                <strong>Account Stripe:</strong>{' '}
                {onboardingStatus.hasAccount ? (
                  <Badge bg="success" className="ms-2">Creato</Badge>
                ) : (
                  <Badge bg="secondary" className="ms-2">Non creato</Badge>
                )}
              </div>

              {onboardingStatus.hasAccount && (
                <>
                  <div className="mb-3">
                    <strong>Onboarding completato:</strong>{' '}
                    {onboardingStatus.onboardingComplete ? (
                      <Badge bg="success" className="ms-2">
                        <i className="bi bi-check-circle me-1"></i>
                        Completato
                      </Badge>
                    ) : (
                      <Badge bg="warning" className="ms-2">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        Da completare
                      </Badge>
                    )}
                  </div>

                  <div className="mb-3">
                    <strong>Pagamenti abilitati:</strong>{' '}
                    {onboardingStatus.chargesEnabled ? (
                      <Badge bg="success" className="ms-2">Sì</Badge>
                    ) : (
                      <Badge bg="danger" className="ms-2">No</Badge>
                    )}
                  </div>

                  <div className="mb-3">
                    <strong>Bonifici abilitati:</strong>{' '}
                    {onboardingStatus.payoutsEnabled ? (
                      <Badge bg="success" className="ms-2">Sì</Badge>
                    ) : (
                      <Badge bg="danger" className="ms-2">No</Badge>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {onboardingStatus && onboardingStatus.onboardingComplete ? (
            <Alert variant="success" className="mt-4">
              <Alert.Heading>
                <i className="bi bi-check-circle me-2"></i>
                Configurazione completata!
              </Alert.Heading>
              <p className="mb-0">
                Il tuo account Stripe è stato configurato correttamente e puoi ricevere pagamenti.
              </p>
            </Alert>
          ) : (
            <Alert variant="info" className="mt-4">
              <Alert.Heading>Azione richiesta</Alert.Heading>
              <p>
                Per ricevere pagamenti dagli eventi, devi completare la configurazione del tuo account Stripe.
                Il processo richiede alcuni minuti e include:
              </p>
              <ul>
                <li>Verifica dell'identità</li>
                <li>Informazioni bancarie per ricevere i pagamenti</li>
                <li>Dettagli dell'organizzazione</li>
              </ul>
              <Button
                style={{ backgroundColor: '#006e39', borderColor: '#006e39' }}
                onClick={startOnboarding}
                disabled={generatingLink}
              >
                {generatingLink ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Generazione link...
                  </>
                ) : (
                  <>
                    <i className="bi bi-arrow-right-circle me-2"></i>
                    {onboardingStatus?.hasAccount ? 'Riprendi configurazione' : 'Inizia configurazione'}
                  </>
                )}
              </Button>
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header>
          <h5 className="mb-0">
            <i className="bi bi-info-circle me-2"></i>
            Come funziona
          </h5>
        </Card.Header>
        <Card.Body>
          <h6>Stripe Connect</h6>
          <p>
            Stripe Connect permette alla piattaforma UISP di gestire pagamenti che vengono
            automaticamente inviati al comitato corretto (regionale, territoriale, ecc.).
          </p>

          <h6 className="mt-3">Processo di configurazione</h6>
          <ol>
            <li>Clicca su "Inizia configurazione" per essere reindirizzato a Stripe</li>
            <li>Completa il form con le informazioni richieste da Stripe</li>
            <li>Verifica la tua identità e aggiungi le informazioni bancarie</li>
            <li>Torna su questa pagina per verificare lo stato</li>
          </ol>

          <h6 className="mt-3">Sicurezza</h6>
          <p className="mb-0">
            <i className="bi bi-shield-check me-2 text-success"></i>
            Stripe è certificato PCI Service Provider Level 1, il più alto livello di
            certificazione nel settore dei pagamenti. I tuoi dati sono al sicuro.
          </p>
        </Card.Body>
      </Card>

      {onboardingStatus && onboardingStatus.hasAccount && (
        <div className="mt-3 text-center">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={checkOnboardingStatus}
          >
            <i className="bi bi-arrow-clockwise me-2"></i>
            Aggiorna stato
          </Button>
        </div>
      )}
    </Container>
  );
};

export default StripeOnboarding;
